import {
  boot, buildAttributes, isIntercomAvailable, loadIntercom, show, update,
} from '@usealloy/intercom';
import type { IntercomSettings } from '@usealloy/intercom';

import { isZendeskLoaded, loadZendesk, openZendeskSupportForm } from '~/helpers/zendesk';
import type { Agent, SupportFormOffset } from '~/helpers/zendesk';

/** Unauthenticated Intercom config. Serves the public app ID only -- no secret, no JWT. */
const CONFIG_URL = '/v3/intercom_config/';

interface IntercomConfigResponse {
  /** Null when the flag is off, or the environment has no Intercom credentials. */
  app_id: string | null;
  customer: {
    organization_name: string;
    organization_name_slug: string;
  } | null;
  environment: string;
}

/** Reads the config, or null if the endpoint could not be reached. Never throws. */
const fetchIntercomConfig = async (): Promise<IntercomConfigResponse | null> => {
  try {
    // AjaxOnlyMixin 404s without this header.
    const response = await fetch(CONFIG_URL, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) return null;

    return await response.json();
  } catch {
    return null;
  }
};

/**
 * Loads the support widget for pages extending daffy/templates/base.html -- the auth flow
 * and the 403/404/500 pages. Intercom or Zendesk, never both.
 *
 * These pages have no agent, so the flag is evaluated anonymously by /v3/intercom_config/.
 * That needs a fetch, which is why neither snippet is a static tag here as it is in
 * v3/templates/dashboard/index.html. Intercom boots without a JWT, creating a Lead that
 * merges into the User record at sign-in.
 */
export const loadPublicSupportWidget = async (): Promise<void> => {
  if (isIntercomAvailable() || isZendeskLoaded()) return;

  const config = await fetchIntercomConfig();

  // Unreachable is not "off" -- falling back to Zendesk here could contradict the flag.
  if (!config) return;

  if (config.app_id) {
    // loadIntercom first: it installs the stub that queues this boot.
    loadIntercom(config.app_id);
    boot(buildAttributes({
      customerName: config.customer?.organization_name,
      customerSlug: config.customer?.organization_name_slug,
      environment: config.environment,
    }));
    return;
  }

  loadZendesk();
};

/**
 * Intercom-only placement override; ignored when Zendesk is loaded. Kept separate from
 * Zendesk's `SupportFormOffset` (string offsets on a two-axis corner) since Intercom's shape
 * doesn't map onto it -- one alignment side plus numeric, always-bottom-relative padding.
 */
export type IntercomPositionOverride =
  Pick<IntercomSettings, 'alignment' | 'horizontal_padding' | 'vertical_padding'>;

interface OpenSupportProps {
  /** Prefills the Zendesk form. Absent on the login page, which has no agent. */
  agent?: Agent;
  offsetOverride?: SupportFormOffset;
  positionOverride?: SupportFormOffset;
  intercomPositionOverride?: IntercomPositionOverride;
  sidebarExpanded?: boolean;
  /**
   * Signed Intercom identity token for the current agent. Read fresh at call time rather
   * than captured earlier -- `exp` follows the Alloy session, so a token minted at boot can
   * be expired by the time someone clicks. Omit on the login page, which is anonymous.
   */
  userJwt?: string;
  /**
   * Called when Intercom's `update` allowance for this page (20 per 30 minutes) is spent.
   * Should essentially never fire -- see `@usealloy/intercom`'s `update` docs -- so a call
   * means a trigger is mis-wired, not something the person using the dashboard caused.
   * `show()` is not called in this case, since Intercom kept its last context and opening
   * now would start a conversation pointing at the wrong screen.
   */
  onRateLimited?: () => void;
}

/**
 * Opens support -- whichever widget this session loaded.
 *
 * `update` doubles as the branch: it refreshes Intercom's context and reports whether that
 * landed. `not-loaded` means Zendesk is live, so fall through to its form; routine during
 * rollout and never an error.
 */
export const openSupport = ({
  agent = { firstName: '', lastName: '', email: '' },
  offsetOverride,
  positionOverride,
  intercomPositionOverride,
  sidebarExpanded = false,
  userJwt,
  onRateLimited,
}: OpenSupportProps = {}) => {
  const outcome = update({
    ...(userJwt ? { intercom_user_jwt: userJwt } : undefined),
    ...intercomPositionOverride,
  });

  if (outcome === 'not-loaded') {
    openZendeskSupportForm(sidebarExpanded, agent, offsetOverride, positionOverride);
    return;
  }

  if (outcome === 'rate-limited') {
    onRateLimited?.();
    return;
  }

  show();
};

export default {
  loadPublicSupportWidget,
  openSupport,
};
